import { useEffect, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { loadGis } from '../../libs/loadGis';
import { useNavigate, useLocation } from 'react-router-dom';

function AuthPage() {
  const { user, ready, loginWithCredential } = useAuth();
  const googleBtnRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  // remplacez par le chemin de votre image si besoin (ex: /octopus.png)
  const LOGO = '/favicon.ico';

  useEffect(() => {
    if (ready && user) navigate(from, { replace: true });
  }, [ready, user, from, navigate]);

  useEffect(() => {
    let mounted = true;

    loadGis()
      .then(() => {
        if (!mounted) return;

        // pas de One Tap
        window.google.accounts.id.disableAutoSelect?.();
        window.google.accounts.id.cancel?.();

        window.google.accounts.id.initialize({
          client_id:
            (typeof import.meta !== 'undefined' &&
              import.meta.env?.VITE_GOOGLE_CLIENT_ID) ||
            process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: async ({ credential }) => {
            try {
              await loginWithCredential(credential);
              navigate(from, { replace: true });
            } catch (e) {
              console.error('Login error', e);
            }
          },
          use_fedcm_for_prompt: true,
          auto_select: false,
          itp_support: true,
          context: 'signin',
        });

        // bouton officiel uniquement (aucun prompt auto)
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          logo_alignment: 'left',
          width: 320,
        });
      })
      .catch(console.error);

    return () => {
      mounted = false;
    };
  }, [loginWithCredential, navigate, from]);

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-[#0c1131] text-white">
      {/* Fond adapté au logo : halos rose + trame légère */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute -top-40 -right-32 h-[34rem] w-[34rem] rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(closest-side, #FC97974a, transparent 70%)',
          }}
        />
        <div
          className="absolute -bottom-44 -left-40 h-[30rem] w-[30rem] rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(closest-side, #FC979733, transparent 70%)',
          }}
        />
        <div className="absolute inset-0 opacity-15 [background:radial-gradient(circle_at_1px_1px,#ffffff12_1px,transparent_1px)] [background-size:28px_28px]" />
      </div>

      {/* Card centrale unique */}
      <main className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/10 p-8 backdrop-blur-xl shadow-2xl">
          {/* Logo centré */}
          <div className="mb-6 flex flex-col items-center">
            <img
              src={LOGO}
              alt="Octopus"
              className="h-16 w-16 rounded-2xl object-cover shadow-lg ring-1 ring-white/10"
            />
            <h1 className="mt-3 text-2xl font-semibold tracking-tight">
              Octopus
            </h1>
          </div>

          <p className="mt-1 mb-6 text-center text-sm text-white/70">
            Utilisez votre compte Google pour vous identifier.
          </p>

          {/* Bouton Google (officiel) */}
          <div className="flex justify-center">
            <div ref={googleBtnRef} />
          </div>

          <div className="my-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-white/40">
              Sécurité
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <ul className="space-y-2 text-center text-sm text-white/60">
            <li>• Vos données sont privées et chiffrées.</li>
          </ul>
        </div>
      </main>

      <footer className="relative z-10 py-6 text-center text-xs text-white/40">
        © {new Date().getFullYear()} Prae — Tous droits réservés
      </footer>
    </div>
  );
}

export default AuthPage;
